<template>
  <div class="login">
    <!-- ─── Brand side: dark boutique panel ─── -->
    <section class="login__brand">
      <div class="login__photo" :style="{ backgroundImage: `url(${boutiquePhoto})` }"></div>
      <div class="login__shade"></div>

      <div class="login__brand-content">
        <img
          :src="is_company_user ? `${mediaUrl + logo_path}` : goldLogo"
          alt="Vastrosa"
          class="login__logo"
        />
        <div class="login__motto">Fashion <span>|</span> Style <span>|</span> You</div>

        <h2 class="login__headline">
          Manage Your<br />Fashion Business
          <span class="login__script">Effortlessly</span>
        </h2>
        <p class="login__lead">
          Track orders, manage inventory, handle customers and grow your
          clothing brand &mdash; all in one place.
        </p>

        <ul class="login__features">
          <li v-for="f in features" :key="f.label">
            <v-icon class="login__feature-icon">{{ f.icon }}</v-icon>
            <span>{{ f.label }}</span>
          </li>
        </ul>
      </div>

      <div class="login__brand-footer">&copy; {{ year }} Vastrosa <span>|</span> Admin Panel</div>
    </section>

    <!-- ─── Form side ─── -->
    <section class="login__panel">
      <div class="login__arc" aria-hidden="true"></div>

      <v-form class="login__card" @submit.prevent="login">
        <div class="login__eyebrow">Admin Panel</div>
        <div class="login__rule"></div>
        <h1 class="login__title">Welcome Back</h1>
        <p class="login__subtitle">Sign in to your Vastrosa admin account</p>

        <label class="login__label" for="login-email">Email Address</label>
        <v-text-field
          id="login-email"
          v-model.trim="form.email"
          type="email"
          autocomplete="username"
          outlined
          dense
          placeholder="you@company.com"
          prepend-inner-icon="mdi-email-outline"
          :error-messages="errors.email"
          class="login__field"
          @input="loginError = ''"
        ></v-text-field>

        <div class="login__label-row">
          <label class="login__label" for="login-password">Password</label>
          <router-link to="/forgot-password" class="login__link">Forgot password?</router-link>
        </div>
        <v-text-field
          id="login-password"
          v-model="form.password"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="current-password"
          outlined
          dense
          placeholder="Enter your password"
          prepend-inner-icon="mdi-lock-outline"
          :append-icon="showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
          :error-messages="errors.password"
          class="login__field"
          @click:append="showPassword = !showPassword"
          @input="loginError = ''"
        ></v-text-field>

        <v-expand-transition>
          <div v-if="loginError" class="login__error" role="alert">
            <v-icon small color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
            {{ loginError }}
          </div>
        </v-expand-transition>

        <v-btn
          type="submit"
          block
          large
          depressed
          class="login__submit"
          :loading="isLoading"
          :disabled="isLoading || !form.email || !form.password"
        >
          Sign In
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>

        <div class="login__card-footer">
          Better Fashion <span>&bull;</span> Better Business
        </div>
      </v-form>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import boutiquePhoto from "@/assets/login-boutique.jpg";
import goldLogo from "@/assets/vastrosa-logo-gold.png";

export default {
  name: "LoginPage",
  data() {
    return {
      form: {
        email: "",
        password: "",
        portal: true,
      },
      loggedInRole: "",
      logo_path: "@assets/vastrosa-logo.png",
      is_company_user: false,
      showPassword: false,
      isLoading: false,
      loginError: "",
      year: new Date().getFullYear(),
      boutiquePhoto,
      goldLogo,
      features: [
        { icon: "mdi-package-variant-closed", label: "Orders" },
        { icon: "mdi-hanger", label: "Inventory" },
        { icon: "mdi-account-group-outline", label: "Customers" },
        { icon: "mdi-chart-bar", label: "Growth" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
  },
  mounted() {
    // Check if company data exists in localStorage
    const companyData = localStorage.getItem("company");
    if (companyData) {
      try {
        const company = JSON.parse(companyData);
        if (company && company.id) {
          this.is_company_user = true;
          this.getCompany(company.id);
        }
      } catch (error) {
        console.error("Error parsing company data:", error);
      }
    }
  },
  methods: {
    async getCompany(companyId) {
      await axios
        .get(`/get_company/${this.encryptIt(companyId)}`)
        .then((company) => {
          company = company.data.data;
          if (company && company.logo_path) {
            this.logo_path = company.logo_path;
          } else {
            this.is_company_user = false; // no logo of its own - use the Vastrosa one
          }
        })
        .catch((error) => {
          this.is_company_user = false;
          console.error("Error fetching data:", error);
        });
    },
    ...mapActions({
      logIn: "auth/logIn",
    }),
    async login() {
      if (this.isLoading || !this.form.email || !this.form.password) return;

      this.isLoading = true;
      this.loginError = "";

      // Portal passwords have always been sent in capitals (the field used to
      // force caps as you typed), and every existing portal account's password
      // is stored that way - so keep sending them in capitals, just without
      // rewriting the field while it is being typed. Email is sent as typed;
      // the lookup is case-insensitive.
      const credentials = {
        ...this.form,
        email: (this.form.email || "").trim(),
        password: (this.form.password || "").toUpperCase(),
      };

      this.logIn(credentials)
        .then(() => {
          this.isLoading = false;

          // Get user position and permissions
          const positionName = this.user.position ? this.user.position.name : "";
          const permissions = this.$store.state.auth.permissions || [];

          // Position-based redirects
          if (positionName === "TRANSPORTER MANAGER") {
            this.$router.push("/transporter-manager/dashboard").catch(() => {});
          } else if (positionName === "CUSTOMER SERVICE MANAGER") {
            // Check if they have TICKETS.VIEW permission
            if (permissions.includes("TICKETS.VIEW")) {
              this.$router.push("/tickets").catch(() => {});
            } else {
              this.$router.push("/unauthorized").catch(() => {});
            }
          } else {
            this.$router.push("/").catch(() => {});
          }
        })
        .catch((error) => {
          const data = (error && error.response && error.response.data) || {};
          this.loginError =
            data.message && !/^validation/i.test(data.message)
              ? data.message
              : "Couldn't sign you in. Check your email and password and try again.";
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.login {
  --gold: #c9a26b;
  --gold-deep: #9a7449;
  --gold-soft: #d8bb8f;
  --espresso: #17120d;
  --cream: #f6f1e9;
  --ink: #2e2a24;

  min-height: 100vh;
  display: flex;
  background: var(--cream);
}

/* ─── Brand side ─── */
.login__brand {
  position: relative;
  flex: 0 0 58%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 72px 7vw 96px;
  background: var(--espresso);
  color: #fff;
  overflow: hidden;
}
.login__photo {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 48%;
  background-size: cover;
  background-position: center;
}
.login__shade {
  /* dark on the left where the text is, opening up over the photo */
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, var(--espresso) 0%, var(--espresso) 46%, rgba(23, 18, 13, 0.55) 62%, rgba(23, 18, 13, 0.05) 100%),
    radial-gradient(60% 50% at 20% 20%, rgba(201, 162, 107, 0.10) 0%, transparent 70%);
}
.login__brand-content {
  position: relative;
  z-index: 1;
  max-width: 440px;
}
.login__logo {
  display: block;
  width: 100%;
  max-width: 360px;
  height: auto;
}
.login__motto {
  margin-top: 10px;
  font-size: 13px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
}
.login__motto span {
  margin: 0 0.6em;
  color: var(--gold);
}
.login__headline {
  margin: 56px 0 0;
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 600;
  font-size: clamp(32px, 2.8vw, 44px);
  line-height: 1.15;
  color: #fff;
}
.login__script {
  display: block;
  margin-top: 4px;
  font-family: "Great Vibes", cursive;
  font-weight: 400;
  font-size: clamp(52px, 4.4vw, 72px);
  line-height: 1;
  color: var(--gold);
}
.login__lead {
  margin: 20px 0 0;
  max-width: 360px;
  font-size: 15px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.82);
}
.login__features {
  display: flex;
  margin: 44px 0 0;
  padding: 0;
  list-style: none;
}
.login__features li {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0 22px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  border-left: 1px solid rgba(255, 255, 255, 0.16);
}
.login__features li:first-child {
  padding-left: 0;
  border-left: 0;
}
.login__feature-icon {
  color: var(--gold) !important;
  font-size: 28px !important;
}
.login__brand-footer {
  position: absolute;
  left: 4vw;
  bottom: 32px;
  z-index: 1;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}
.login__brand-footer span {
  margin: 0 0.5em;
  color: rgba(255, 255, 255, 0.35);
}

/* ─── Form side ─── */
.login__panel {
  position: relative;
  flex: 1 1 42%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 32px;
  background:
    radial-gradient(70% 60% at 80% 10%, #fffdf9 0%, transparent 70%),
    var(--cream);
  overflow: hidden;
}
.login__arc {
  /* thin gold arc sweeping in from the bottom right, like the mockup */
  position: absolute;
  width: 760px;
  height: 760px;
  right: -520px;
  bottom: -260px;
  border-radius: 50%;
  border: 2px solid rgba(201, 162, 107, 0.55);
  pointer-events: none;
}
.login__card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 470px;
  padding: 52px 48px 36px;
  border-radius: 20px;
  background: rgba(253, 251, 247, 0.92);
  border: 1px solid rgba(201, 162, 107, 0.18);
  box-shadow: 0 2px 6px rgba(46, 42, 36, 0.04), 0 24px 60px rgba(46, 42, 36, 0.10);
}
.login__eyebrow {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--gold-deep);
}
.login__rule {
  width: 32px;
  height: 1.5px;
  margin: 14px auto 18px;
  background: var(--gold);
}
.login__title {
  margin: 0;
  text-align: center;
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 600;
  font-size: 36px;
  line-height: 1.2;
  color: var(--ink);
}
.login__subtitle {
  margin: 10px 0 36px;
  text-align: center;
  font-size: 15px;
  color: rgba(46, 42, 36, 0.62);
}
.login__label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.login__label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
}
.login__link {
  font-size: 13px;
  font-weight: 600;
  color: var(--gold-deep);
  text-decoration: none;
}
.login__link:hover,
.login__link:focus-visible {
  text-decoration: underline;
}
.login__field ::v-deep .v-input__slot {
  min-height: 50px !important;
  border-radius: 10px !important;
  background: #fff !important;
}
.login__field ::v-deep fieldset {
  border-color: #ddd3c4 !important;
}
.login__field.v-input--is-focused ::v-deep fieldset {
  border-color: var(--gold-deep) !important;
  border-width: 2px !important;
}
.login__field ::v-deep .v-input__prepend-inner .v-icon,
.login__field ::v-deep .v-input__append-inner .v-icon {
  color: rgba(46, 42, 36, 0.55) !important;
}
.login__error {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  margin: -4px 0 16px;
  border-radius: 8px;
  background: rgba(216, 86, 74, 0.08);
  color: var(--color-error);
  font-size: 13px;
  line-height: 1.4;
}
.login__submit {
  height: 52px !important;
  margin-top: 6px;
  border-radius: 10px !important;
  background: linear-gradient(90deg, #8a6640 0%, #b48a5a 55%, #c9a26b 100%) !important;
  color: #fff !important;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  box-shadow: 0 8px 20px rgba(138, 102, 64, 0.28) !important;
}
.login__submit.v-btn--disabled {
  background: #e6ded2 !important;
  color: rgba(46, 42, 36, 0.4) !important;
  box-shadow: none !important;
}
.login__submit.v-btn--disabled ::v-deep .v-icon {
  color: rgba(46, 42, 36, 0.4) !important;
}
.login__card-footer {
  margin-top: 36px;
  padding-top: 24px;
  border-top: 1px solid rgba(46, 42, 36, 0.08);
  text-align: center;
  font-size: 12px;
  letter-spacing: 0.12em;
  color: rgba(46, 42, 36, 0.55);
}
.login__card-footer span {
  margin: 0 0.6em;
  color: var(--gold);
}

/* ─── Laptop widths: tighter brand side ─── */
@media (max-width: 1280px) {
  .login__brand {
    flex-basis: 52%;
    padding-left: 5vw;
    padding-right: 5vw;
  }
  .login__photo {
    width: 40%;
  }
  .login__features li {
    padding: 0 14px;
  }
}

/* ─── Tablets / phones: brand header on top, form below ─── */
@media (max-width: 959px) {
  .login {
    flex-direction: column;
  }
  .login__brand {
    flex: 0 0 auto;
    padding: 40px 24px 36px;
    text-align: center;
  }
  .login__photo {
    width: 100%;
  }
  .login__shade {
    background: rgba(23, 18, 13, 0.78);
  }
  .login__brand-content {
    max-width: none;
  }
  .login__logo {
    max-width: 240px;
    margin: 0 auto;
  }
  .login__headline,
  .login__lead,
  .login__features,
  .login__brand-footer {
    display: none;
  }
  .login__panel {
    flex: 1 1 auto;
    align-items: flex-start;
    padding: 28px 16px 48px;
  }
  .login__arc {
    display: none;
  }
  .login__card {
    padding: 36px 24px 28px;
  }
}
</style>
